import { type Capability, capabilitiesIn } from "./capabilities.ts";

export type Preset = { name: string; capabilities: Capability[]; says: string; briefs: string };

export const PRESETS: readonly Preset[] = [
  {
    name: "assistant",
    capabilities: ["read", "web"],
    says: "Answers in the conversation and hands pieces to whoever else is here.",
    briefs:
      "You answer the last thing said, in one turn. When a job belongs to somebody else here, hand it to them and say so. Short lines, no preamble.",
  },
  {
    name: "researcher",
    capabilities: ["read", "web"],
    says: "Searches the repository, the machine, and the open web. Never edits.",
    briefs:
      "You find things out. Read the repository, the machine and the open web, then answer with what you found and where it came from. Cite the file or the link. You never edit anything.",
  },
  {
    name: "builder",
    capabilities: ["read", "write", "shell"],
    says: "The engineer. The only preset that edits.",
    briefs:
      "You do the work in the repository: read it, change it, run its checks, commit and push. Small diffs, one unit at a time, and the gate green before you say it is done.",
  },
  {
    name: "reviewer",
    capabilities: ["read", "shell"],
    says: "Verifies what a builder delivered. Never edits.",
    briefs:
      "You check what was delivered against what was asked. Run the checks yourself, say what is wrong first, and name the proof for anything you call done. You never edit.",
  },
];

export function presetNamed(name: string): Preset | undefined {
  return PRESETS.find((preset) => preset.name === name);
}

export function holdsNoWorkGrants(capabilities: readonly string[]): boolean {
  return capabilitiesIn(capabilities).length === 0;
}

export const CHECK_INS = ["5m", "10m", "20m", "40m", "60m", "90m", "120m"] as const;
export type CheckIn = (typeof CHECK_INS)[number];

export const A_CHECK_IN_BY_DEFAULT: CheckIn = "5m";

export function isACheckIn(said: unknown): said is CheckIn {
  return typeof said === "string" && (CHECK_INS as readonly string[]).includes(said);
}

export function minutesOf(checkIn: CheckIn): number {
  return Number(checkIn.slice(0, -1));
}

export const EFFORTS = ["off", "on", "low", "medium", "high", "max"] as const;
export type Asked = (typeof EFFORTS)[number];

const HOW_HARD: Record<Asked, number> = { off: 0, on: 1, low: 2, medium: 3, high: 4, max: 5 };

export function isAnEffort(said: unknown): said is Asked {
  return typeof said === "string" && (EFFORTS as readonly string[]).includes(said);
}

export function asHardAs(asked: unknown, ceiling: string, offered: readonly string[]): string {
  const wanted = isAnEffort(asked) ? asked : isAnEffort(ceiling) ? ceiling : "off";
  const capped = HOW_HARD[wanted] <= HOW_HARD[isAnEffort(ceiling) ? ceiling : "off"] ? wanted : ceiling;
  const may = offered.filter(isAnEffort);
  if (may.length === 0) return "off";
  if (may.includes(capped as Asked)) return capped;
  const under = may.filter((one) => HOW_HARD[one] <= HOW_HARD[capped as Asked]);
  return under.sort((one, two) => HOW_HARD[two] - HOW_HARD[one])[0] ?? (may[0] as string);
}

export type TeamPreset = {
  name: string;
  says: string;
  forWhat: "personal" | "business";
  members: ReadonlyArray<{ handle: string; capabilities: string; says: string }>;
};

export const TEAM_PRESETS: readonly TeamPreset[] = [
  {
    name: "trip",
    says: "Planning a trip",
    forWhat: "personal",
    members: [
      { handle: "planner", capabilities: "", says: "talks to everyone, does nothing" },
      { handle: "flights", capabilities: "read,web", says: "routes, times, prices" },
      { handle: "stays", capabilities: "read,web", says: "places to sleep" },
      { handle: "onsite", capabilities: "read,web", says: "what to do there" },
    ],
  },
  {
    name: "household",
    says: "Running a household",
    forWhat: "personal",
    members: [
      { handle: "keeper", capabilities: "", says: "the one you talk to" },
      { handle: "errands", capabilities: "read,web", says: "finds and compares" },
      { handle: "money", capabilities: "read", says: "reads statements, totals things" },
    ],
  },
  {
    name: "sales",
    says: "Sales and marketing",
    forWhat: "business",
    members: [
      { handle: "chief", capabilities: "", says: "takes the ask, names the worker" },
      { handle: "market", capabilities: "read,web", says: "who they are, what they said" },
      { handle: "deck", capabilities: "read,write", says: "writes the thing" },
      { handle: "brand", capabilities: "read", says: "refuses what is off-voice" },
    ],
  },
  {
    name: "product",
    says: "Product, design, engineering",
    forWhat: "business",
    members: [
      { handle: "lead", capabilities: "", says: "one voice to the room" },
      { handle: "research", capabilities: "read,web", says: "what others do" },
      { handle: "builder", capabilities: "read,write,shell", says: "makes it work" },
      { handle: "reviewer", capabilities: "read,shell", says: "says pass or not" },
    ],
  },
  {
    name: "solo",
    says: "One agent, no team",
    forWhat: "personal",
    members: [{ handle: "assistant", capabilities: "read,web", says: "answers you directly" }],
  },
];

export function teamPresetsFor(forWhat: "personal" | "business"): readonly TeamPreset[] {
  return TEAM_PRESETS.filter((one) => one.forWhat === forWhat);
}
